import { Link, useParams } from "react-router-dom";
import SeoMeta from "@/components/feature/SeoMeta";
import ReliabilityBadge from "@/components/feature/ReliabilityBadge";
import ArticleCard from "@/components/feature/ArticleCard";
import ReadingProgress from "@/pages/article/components/ReadingProgress";
import ArticleBody from "@/pages/article/components/ArticleBody";
import { formatHeat, getArticleBySlug, getRelated } from "@/lib/articles";
import { trending } from "@/mocks/trending";

function formatDate(date: string) {
  const [y, m, d] = date.split("-");
  return `${y}年${Number(m)}月${Number(d)}日`;
}

export default function ArticleDetail() {
  const { slug = "" } = useParams();
  const article = getArticleBySlug(slug);

  if (!article) {
    return (
      <div className="mx-auto flex max-w-2xl flex-col items-center px-4 py-24 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-400">
          <i className="ri-file-search-line" aria-hidden="true" />
        </span>
        <h1 className="mt-6 font-heading text-2xl font-black text-foreground-950">这条爆料不存在或已下线</h1>
        <p className="mt-3 text-sm text-foreground-600">可能链接有误，去首页看看今天最新的内容。</p>
        <Link
          to="/"
          className="mt-6 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-label font-bold text-background-50"
        >
          返回首页
          <i className="ri-arrow-right-line" aria-hidden="true" />
        </Link>
      </div>
    );
  }

  const related = getRelated(article, 3);

  return (
    <>
      <SeoMeta
        title={`${article.title} - 独家大瓜首发追踪`}
        description={`${article.summary.slice(0, 110)}`}
        keywords={`${article.topicName},${article.tags.join(",")},独家大瓜,独家大瓜最新,大瓜爆料`}
        path={`/article/${article.slug}`}
        type="article"
        image={article.cover}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: article.title,
            description: article.summary,
            image: article.cover,
            datePublished: article.publishedAt,
            dateModified: article.publishedAt,
            inLanguage: "zh-CN",
            articleSection: article.topicName,
            keywords: article.tags.join(","),
            author: { "@type": "Organization", name: "独家大瓜" },
            publisher: { "@type": "Organization", name: "独家大瓜" },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "/" },
              { "@type": "ListItem", position: 2, name: article.topicName, item: `/topic/${article.topic}` },
              { "@type": "ListItem", position: 3, name: article.title, item: `/article/${article.slug}` },
            ],
          },
        ]}
      />
      <ReadingProgress />

      <nav aria-label="面包屑" className="border-b border-background-200 bg-background-100">
        <ol className="shell flex flex-wrap items-center gap-2 px-4 py-3 text-xs text-foreground-500 md:px-6">
          <li>
            <Link to="/" className="transition-colors hover:text-primary-600 cursor-pointer">
              首页
            </Link>
          </li>
          <li aria-hidden="true">
            <i className="ri-arrow-right-s-line" />
          </li>
          <li>
            <Link to={`/topic/${article.topic}`} className="transition-colors hover:text-primary-600 cursor-pointer">
              {article.topicName}
            </Link>
          </li>
          <li aria-hidden="true">
            <i className="ri-arrow-right-s-line" />
          </li>
          <li className="line-clamp-1 max-w-[60%] font-medium text-foreground-800">{article.title}</li>
        </ol>
      </nav>

      <div className="shell grid grid-cols-1 gap-10 px-4 py-10 md:px-6 md:py-14 lg:grid-cols-3 lg:gap-12">
        <article className="lg:col-span-2">
          <div className="flex flex-wrap items-center gap-2.5">
            <ReliabilityBadge value={article.reliability} size="md" />
            <Link
              to={`/topic/${article.topic}`}
              className="cursor-pointer rounded-full bg-secondary-100 px-3 py-1 text-xs font-label font-bold text-secondary-900 transition-colors hover:bg-secondary-200"
            >
              {article.topicName}
            </Link>
            {article.isHeadline && (
              <span className="inline-flex items-center gap-1 rounded-full bg-accent-100 px-3 py-1 text-xs font-label font-bold text-accent-900">
                <i className="ri-vip-crown-2-fill" aria-hidden="true" />
                头条
              </span>
            )}
          </div>

          <h1 className="mt-4 font-heading text-2xl font-black leading-tight text-foreground-950 md:text-4xl">
            {article.title}
          </h1>

          <p className="mt-4 border-l-2 border-primary-300 pl-4 text-sm leading-relaxed text-foreground-600 md:text-[15px]">
            {article.summary}
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 border-y border-background-200 py-4 text-xs text-foreground-500 font-label">
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-user-3-line" aria-hidden="true" />
              {article.author}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-calendar-line" aria-hidden="true" />
              {formatDate(article.publishedAt)} {article.time}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-timer-line" aria-hidden="true" />
              约 {article.readMinutes} 分钟读完
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-eye-line" aria-hidden="true" />
              {article.views} 阅读
            </span>
            <span className="inline-flex items-center gap-1.5 font-bold text-primary-600">
              <i className="ri-fire-fill" aria-hidden="true" />
              热度 {formatHeat(article.heat)}
            </span>
          </div>

          <figure className="mt-7 overflow-hidden rounded-lg">
            <img
              src={article.cover}
              alt={article.title}
              title={`${article.title} - 独家大瓜`}
              className="h-64 w-full object-cover object-top md:h-[420px]"
            />
            <figcaption className="mt-2 text-xs text-foreground-500">
              {article.topicName} · {article.title}
            </figcaption>
          </figure>

          <div className="mt-8">
            <ArticleBody blocks={article.body} />
          </div>

          <div className="mt-9 flex flex-wrap items-center gap-2.5">
            <span className="text-xs font-label font-bold text-foreground-600">相关话题</span>
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="rounded-full border border-background-300 px-3 py-1 text-xs text-foreground-700"
              >
                #{tag}
              </span>
            ))}
          </div>

          <section className="mt-10 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6" aria-label="追踪时间线">
            <div className="mb-5 flex items-center justify-between gap-3">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-history-line text-primary-500" aria-hidden="true" />
                追踪时间线
              </h2>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-[11px] font-label font-bold text-primary-700">
                <span className="h-1.5 w-1.5 rounded-full bg-primary-500" aria-hidden="true" />
                持续更新
              </span>
            </div>
            <ol className="relative space-y-6 border-l border-background-300 pl-6">
              {article.timeline.map((node, index) => (
                <li key={node.time} className="relative">
                  <span
                    className={`absolute -left-[31px] top-1 flex h-4 w-4 items-center justify-center rounded-full ${
                      index === article.timeline.length - 1
                        ? "bg-primary-500 ring-4 ring-primary-100"
                        : "bg-background-300"
                    }`}
                    aria-hidden="true"
                  />
                  <span className="block text-[11px] font-label font-bold tracking-wide text-primary-600">
                    {node.time}
                  </span>
                  <h3 className="mt-1.5 font-heading text-base font-bold text-foreground-950">{node.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{node.desc}</p>
                </li>
              ))}
            </ol>
          </section>

          <section className="mt-12" aria-label="相关推荐">
            <h2 className="mb-5 flex items-center gap-2 font-heading text-xl font-black text-foreground-950">
              <span className="h-5 w-1 flex-shrink-0 rounded-full bg-accent-500" aria-hidden="true" />
              相关推荐
            </h2>
            <div className="flex flex-col gap-4">
              {related.map((item) => (
                <ArticleCard key={item.slug} article={item} layout="horizontal" />
              ))}
            </div>
          </section>
        </article>

        <aside className="lg:col-span-1">
          <div className="sticky top-28 flex flex-col gap-6">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="mb-4 flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-fire-line text-primary-500" aria-hidden="true" />
                今日热榜
              </h2>
              <ol className="flex flex-col gap-3.5">
                {trending.slice(0, 5).map((item) => (
                  <li key={item.slug}>
                    <Link to={`/article/${item.slug}`} className="group flex cursor-pointer items-start gap-3">
                      <span
                        className={`mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md font-label text-xs font-black ${
                          item.rank <= 3 ? "bg-primary-500 text-background-50" : "bg-background-200 text-foreground-700"
                        }`}
                      >
                        {item.rank}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm leading-snug text-foreground-900 transition-colors group-hover:text-primary-600 line-clamp-2">
                          {item.title}
                        </span>
                        <span className="mt-1 block text-[11px] text-foreground-500">
                          {item.tag} · {formatHeat(item.heat)}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ol>
            </div>

            <div className="rounded-lg bg-primary-500 p-5 text-background-50">
              <h2 className="font-heading text-lg font-bold">不错过后续更新</h2>
              <p className="mt-2 text-sm leading-relaxed text-background-100">
                订阅爆料速递，重要事件出现新节点时会第一时间提醒你。
              </p>
              <Link
                to="/about#contact"
                className="mt-4 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full bg-background-50 px-5 py-2.5 text-sm font-label font-bold text-primary-700 transition-colors hover:bg-accent-300"
              >
                订阅爆料速递
                <i className="ri-arrow-right-line" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </aside>
      </div>
    </>
  );
}